import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useChatSocket } from '../hooks/useChatSocket';
import { useCapabilities } from '../hooks/useCapabilities';
import { useFeature } from '../hooks/useEntitlements';
import { featureDisabledMessage, type ChatMessageDto } from '@aiai/shared';
import { createChatSession, deleteChatSession, fetchChatSessions } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { ChatThread } from './ChatThread';
import { ChatSessionRail } from './ChatSessionRail';
import { Callout } from './Callout';
import { ChatNotifyToggle } from './ChatNotifyToggle';
import { maybeNotifyPmMessage } from '../lib/chatNotifications';

/**
 * The chat page's working area: conversation-history rail (left, md+) plus the
 * active conversation. Session selection lives in the URL (`basePath/:sessionId`;
 * the reserved segment `new` is an unsent draft), so conversations are
 * linkable and back/forward work.
 */
export function ChatPanel({
  projectId,
  basePath,
  sessionParam,
  projectName,
}: {
  projectId: string;
  /** Absolute path of the chat page (no trailing slash), e.g. /w/acme/projects/OMHB/chat. */
  basePath: string;
  /** The `:sessionId` URL segment: a session id, 'new' (draft), or undefined. */
  sessionParam: string | undefined;
  /** Shown in desktop notification titles. */
  projectName?: string;
}) {
  const { can } = useCapabilities();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The active session is resolved below; the socket callback reads it via a ref.
  const activeSessionRef = useRef<string | null>(null);
  const onPmMessage = useCallback(
    (message: ChatMessageDto) => {
      maybeNotifyPmMessage(message, {
        activeSessionId: activeSessionRef.current,
        projectName,
        href: `${basePath}/${message.sessionId}`,
        onNavigate: (href) => navigate(href),
      });
    },
    [basePath, navigate, projectName],
  );
  const { thinkingSessionId, subscribeFailed, failedSessionId } = useChatSocket(projectId, onPmMessage);
  const chatOn = useFeature('chat');
  const readOnly = !can('chat:post', { projectId }) || chatOn === false;
  const readOnlyMessage = chatOn === false ? featureDisabledMessage('chat') : undefined;
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const sessionsQuery = useQuery({
    queryKey: qk.chatSessions(projectId),
    queryFn: () => fetchChatSessions(projectId),
    enabled: !!projectId,
  });
  const sessions = useMemo(() => sessionsQuery.data ?? [], [sessionsQuery.data]);

  // Resolve the active conversation: explicit draft, explicit session, or the
  // most recent one. With no history at all, land in draft mode.
  const isDraft = sessionParam === 'new' || (sessionParam === undefined && sessions.length === 0);
  const activeSessionId = isDraft
    ? null
    : (sessionParam ?? sessions[0]?.id ?? null);
  activeSessionRef.current = activeSessionId;

  // A deleted/foreign session id in the URL: fall back to the latest
  // conversation once the list is known.
  const sessionMissing =
    !isDraft &&
    sessionParam !== undefined &&
    sessionsQuery.isSuccess &&
    !sessions.some((s) => s.id === sessionParam);

  const createMutation = useMutation({
    mutationFn: (body: string) => createChatSession(projectId, body),
    onSuccess: ({ session }) => {
      void queryClient.invalidateQueries({ queryKey: qk.chat(projectId) });
      navigate(`${basePath}/${session.id}`, { replace: true });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) => deleteChatSession(projectId, sessionId),
    onSuccess: (_, sessionId) => {
      setDeleteError(null);
      void queryClient.invalidateQueries({ queryKey: qk.chat(projectId) });
      if (sessionId === activeSessionId) navigate(basePath, { replace: true });
    },
    onError: (err) => setDeleteError(errorMessage(err, 'Could not delete the conversation')),
  });

  if (sessionMissing) {
    return (
      <Callout tone="danger">
        That conversation no longer exists.{' '}
        <button type="button" className="underline" onClick={() => navigate(basePath, { replace: true })}>
          Back to chat
        </button>
      </Callout>
    );
  }

  const showThinking = thinkingSessionId === '*' || (activeSessionId !== null && thinkingSessionId === activeSessionId);
  const showFailed = failedSessionId === '*' || (activeSessionId !== null && failedSessionId === activeSessionId);

  return (
    <div className="flex h-[calc(100dvh-18rem)] min-h-[420px] flex-col gap-2">
      {subscribeFailed && (
        <Callout tone="danger">
          Live updates unavailable — refresh to retry.
        </Callout>
      )}

      <ChatNotifyToggle />

      {/* Mobile conversation picker (the rail is md+ only). */}
      <div className="flex items-center gap-2 md:hidden">
        <select
          className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-2.5 py-2 text-sm text-ink"
          value={isDraft ? 'new' : (activeSessionId ?? '')}
          onChange={(e) =>
            navigate(e.target.value === 'new' ? `${basePath}/new` : `${basePath}/${e.target.value}`)
          }
          aria-label="Conversation"
        >
          {(isDraft || sessions.length === 0) && <option value="new">New conversation</option>}
          {sessions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title || 'New conversation'}
            </option>
          ))}
        </select>
        {!readOnly && !isDraft && (
          <button type="button" className="btn-secondary shrink-0" onClick={() => navigate(`${basePath}/new`)}>
            New chat
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <aside className="hidden w-60 shrink-0 md:block">
          <ChatSessionRail
            sessions={sessions}
            activeSessionId={activeSessionId}
            isDraft={isDraft}
            isLoading={sessionsQuery.isLoading}
            readOnly={readOnly}
            deleteBusy={deleteMutation.isPending}
            deleteError={deleteError}
            onSelect={(id) => navigate(`${basePath}/${id}`)}
            onNewChat={() => navigate(`${basePath}/new`)}
            onDelete={(id) => deleteMutation.mutateAsync(id).then(() => undefined)}
          />
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ChatThread
            projectId={projectId}
            sessionId={activeSessionId}
            onFirstMessage={async (body) => {
              await createMutation.mutateAsync(body);
            }}
            readOnly={readOnly}
            readOnlyMessage={readOnlyMessage}
            isPmThinking={showThinking}
            lastTurnFailed={showFailed}
          />
        </div>
      </div>
    </div>
  );
}
